import ServiceClosingProof from '@/components/service/service-closing-proof'
import { serviceLinkMatrix } from '@/constants/service-link-matrix'
import { useRouter } from 'next/router'
import BookButton from '@/components/ui/BookButton'
import FooterLinks from './sections/footer-links'
import ServiceRelatedLinks from './service-related-links'
import NewsletterSignup from './newsletter-signup'
import { usePlausible } from 'next-plausible'
import { ArrowRight, Phone, ShieldCheck } from 'lucide-react'
import HeroMesh from './hero-mesh'
import { PhoneIcon } from '@heroicons/react/24/solid'

export default function Footer({
  usesServiceTemplate = false,
  hideCTA = false,
}) {
  const router = useRouter()
  const plausible = usePlausible()
  const servicePath =
    router.pathname === '/ups/[problem]'
      ? `/ups/${router.query.problem}`
      : router.pathname === '/[commercialService]'
        ? `/${router.query.commercialService}`
        : router.pathname
  const hideServiceExtras = servicePath === '/tampa'
  const isServicePage = Object.prototype.hasOwnProperty.call(
    serviceLinkMatrix,
    servicePath,
  )

  const serviceCTAs = {
    '/web-design': {
      heading: 'Transform Your Online Presence',
      description:
        'Our web design services create visually stunning, user-friendly sites that drive conversions. Let’s build something great together.',
      buttonText: 'Get a Free Web Design Quote',
    },
    '/seo': {
      heading: 'Dominate Search Rankings',
      description:
        'Our SEO expertise will get your site found by the right audience. Enhance your online visibility with targeted strategies.',
      buttonText: 'Boost My Rankings',
    },
    '/vip-support': {
      heading: 'Experience VIP Support',
      description:
        'Get premium support and tailored solutions designed to keep your business running smoothly.',
      buttonText: 'Book a 15‑min strategy call',
    },
    '/paid-search': {
      heading: 'Maximize Your PPC ROI',
      description:
        'Our paid search campaigns are optimized to drive targeted traffic and deliver measurable results. Let’s amplify your ad spend.',
      buttonText: 'Book a Free PPC Consultation',
    },
    '/social-media-marketing': {
      heading: 'Engage Your Audience',
      description:
        'From creative content to strategic ad placements, our social media marketing services will connect your brand with a wider audience.',
      buttonText: 'Book a Free Social Media Consultation',
    },
    '/email-marketing': {
      heading: 'Elevate Your Email & SMS Campaigns',
      description:
        'Drive engagement and conversions with our expertly crafted email marketing strategies. We create emails that resonate.',
      buttonText: 'Book a Free Email Consultation',
    },
    '/bigcommerce': {
      heading: 'Power Your BigCommerce Store',
      description:
        'Get a custom BigCommerce solution tailored to your business needs. Boost sales with a site designed for performance and scalability.',
      buttonText: 'Book a Free BigCommerce Consultation',
    },
    '/shopify': {
      heading: 'Build a Stunning Shopify Store',
      description:
        'Our Shopify expertise will help you create an online store that not only looks great but converts visitors into loyal customers.',
      buttonText: 'Book a Free Shopify Consultation',
    },
    '/bigcommerce-to-shopify': {
      heading: 'Ready to Transform Your Store?',
      description:
        'Let our experts handle the heavy lifting while you grow your business.',
      buttonText: 'Migrate My Store Now',
    },
    '/ups': {
      heading: 'Ready to solve your website and online sales problems?',
      description:
        'Bring your website and invite your UPS Account Manager for a free 15-minute consultation.',
      buttonText: 'Schedule a Meeting',
      link: 'https://calendly.com/d/dtf-kzx-j3m',
    },
    '/ups/website-looks-outdated': {
      heading: 'Let us look at your website together.',
      description:
        'Bring your website and invite your UPS Account Manager. In a free 15-minute meeting, we can discuss what needs to change and how your UPS benefits may help.',
      buttonText: 'Book a website review',
      link: 'https://calendly.com/d/dtf-kzx-j3m',
    },
  }

  const defaultCTA = {
    heading: 'Contact us today',
    description:
      'Ventura is ready to help you achieve your goals. Contact us now to get started.',
    buttonText: 'Book a Consultation',
  }

  const currentCTA =
    serviceCTAs[servicePath] ||
    (servicePath.startsWith('/ups/') ? serviceCTAs['/ups'] : defaultCTA)

  return (
    <div>
      <div className="rounded-3xl bg-white">
        {(router.pathname === '/about' || router.pathname === '/') && (
          <ServiceClosingProof
            afterDivider
            compactBottom={router.pathname === '/about'}
          />
        )}

        {isServicePage && !hideServiceExtras && (
          <ServiceClosingProof includeCaseStudies={!usesServiceTemplate} />
        )}

        {/* <NewsletterSignup /> */}

        {hideCTA || hideServiceExtras ||
        router.pathname.includes('/tools') ||
        router.pathname.includes('/contact-us') ||
        router.pathname.includes('/thank-you') ||
        router.pathname.includes('/your-questionnaire-has-been-submitted') ||
        router.pathname.includes('/careers') ||
        router.pathname.includes('/case-study') ||
        router.pathname === '/case-studies' ? null : (
          <div className="mx-auto w-full max-w-screen-xl px-5 md:px-10">
            <section className="hero-mesh relative overflow-hidden rounded-3xl bg-gradient-to-br from-blue-50 via-white to-blue-50 px-5 py-16 lg:py-20">
              <HeroMesh scheme="blue" edgeOnly />
              <div className="relative z-10 mx-auto max-w-4xl text-center">
                <h2 className="mb-6 text-3xl font-bold text-black sm:mb-8 sm:text-4xl lg:text-5xl">
                  {currentCTA.heading}
                </h2>
                <p className="mb-8 text-lg leading-relaxed text-black sm:mb-10 sm:text-xl">
                  {currentCTA.description}
                </p>

                <div className="flex flex-col justify-center gap-4 sm:flex-row sm:gap-0">
                  <BookButton
                    color="blue"
                    text={currentCTA.buttonText}
                    link={currentCTA.link}
                    custom="!text-center flex justify-center px-8 py-4 text-lg font-semibold"
                    right
                    icon={<ArrowRight className="!size-6" />}
                    onClick={() => {
                      if (router.pathname === '/web-design') {
                        if (
                          typeof window !== 'undefined' &&
                          typeof window.fbq !== 'undefined'
                        ) {
                          window.fbq(
                            'track',
                            'WebDesignFooterGetWebDesignQuoteClick',
                          )
                        }
                        if (plausible)
                          plausible('WebDesignFooterGetWebDesignQuoteClick')
                      }
                    }}
                  />

                  {/* <BookButton
                  color="ghost"
                  text="(813) 556-7990"
                  link="tel:8135567990"
                  custom={router.pathname === '/' ? 'mt-2' : "!text-center flex justify-center px-8 py-4 text-lg font-semibold"}
                  left
                  icon={<PhoneIcon />}
                  onClick={() => {
                    if (router.pathname === '/web-design') {
                      if (typeof window !== 'undefined' && typeof window.fbq !== 'undefined') {
                        window.fbq('track', 'WebDesignFooterCallButtonClick')
                      }
                      if (plausible) plausible('WebDesignFooterCallButtonClick')
                    }
                  }}
                /> */}
                </div>

                {/* <div className="text-center text-black">
                <p className="text-sm sm:text-base">
                  <ShieldCheck className="relative bottom-px mr-2 inline" />
                  No long-term contracts • Cancel anytime
                </p>
              </div> */}
              </div>
            </section>
          </div>
        )}

        {!hideServiceExtras && <ServiceRelatedLinks path={servicePath} />}
        <FooterLinks flush={hideServiceExtras || servicePath === '/ups/shopify'} />
      </div>
    </div>
  )
}
